import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";

import { RebuildHeader } from "@/components/layout/RebuildHeader";
import { ChevronDown, ChevronRight, BarChart3 } from "lucide-react";
import analyticsProvider from "@/providers/analytics.provider";
import type { IInsuranceApprovedEstimate, IApprovedLineItem } from "@/interfaces/analytics";
import { useSelector } from "@/redux/store";
import { appSelector } from "@/redux/selectors/app.selector";
import AuthProvider from "@/providers/auth.provider";
import type { IOrganizationObject } from "@/interfaces/organization";

const pageSize = 10;

const LineItemRow = ({ item }: { item: IApprovedLineItem }) => (
  <div className="m-2 rounded-md border-1 border-gray-100 bg-white px-4 py-3">
    <div className="flex items-start justify-between">
      <div className="flex-1">
        <div className="text-sm font-bold text-gray-900">{item.description}</div>
        <div className="mt-1 text-xs text-gray-500">
          Room: <span className="rounded-md bg-gray-100 px-1 font-bold">{item.room_name}</span> • Xactimate Codes:{" "}
          <span className="rounded-md bg-gray-100 px-1 font-bold">{item.cat}</span>,{" "}
          <span className="rounded-md bg-gray-100 px-1 font-bold">{item.sel}</span>
        </div>
      </div>
      <div className="ml-8 text-right text-sm font-bold whitespace-nowrap text-green-600">${item.total?.toLocaleString() || "0"}</div>
    </div>
  </div>
);

export const AnalyticsPage = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [allEstimates, setAllEstimates] = useState<IInsuranceApprovedEstimate[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);

  // Organization-related state
  const [organizations, setOrganizations] = useState<IOrganizationObject[]>([]);
  const [selectedOrganization, setSelectedOrganization] = useState<string>("");
  const [isLoadingOrganizations, setIsLoadingOrganizations] = useState(false);

  // User role selectors
  const isCreator = useSelector(appSelector.isCreator);
  const userOrganizationId = useSelector(appSelector.organizationId);

  // Fetch organizations for creator users
  useEffect(() => {
    if (!isCreator) return;

    const fetchOrganizations = async () => {
      setIsLoadingOrganizations(true);
      try {
        const response = await AuthProvider.listOrganizations();
        if (response.success) {
          setOrganizations(response.data.organizations);
          // Auto-select user's organization if available
          if (userOrganizationId) {
            setSelectedOrganization(userOrganizationId);
          }
        }
      } catch (error) {
        console.error("Failed to fetch organizations:", error);
      } finally {
        setIsLoadingOrganizations(false);
      }
    };

    fetchOrganizations();
  }, [isCreator, userOrganizationId]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        // Pass selectedOrganization to the analytics API if user is creator
        const params = isCreator && selectedOrganization ? { organizationId: selectedOrganization } : {};
        const response = await analyticsProvider.getApprovedEstimates(params);
        setAllEstimates(response.data.estimates);
      } catch (error) {
        console.error("Failed to fetch analytics data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [selectedOrganization, isCreator]);

  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Client-side filtering and pagination
  const filteredEstimates = allEstimates.filter(
    (estimate: IInsuranceApprovedEstimate) =>
      (estimate.rebuildEstimate && estimate.rebuildEstimate.projectName?.toLowerCase().includes(searchQuery.toLowerCase())) ||
      estimate.rebuildEstimateId.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const totalPages = Math.ceil(filteredEstimates.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedEstimates = filteredEstimates.slice(startIndex, endIndex);

  const toggleRowExpansion = (estimateId: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(estimateId)) {
      newExpanded.delete(estimateId);
    } else {
      newExpanded.add(estimateId);
    }
    setExpandedRows(newExpanded);
  };

  if (isLoading) {
    return (
      <div className="flex h-full w-full flex-col bg-gray-50">
        <div className="flex-1 space-y-6 overflow-y-auto p-6">
          <div className="flex items-center space-x-2">
            <BarChart3 className="h-6 w-6 text-blue-500" />
            <h1 className="text-2xl font-bold">Value Add</h1>
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <Card key={i}>
                <CardHeader className="pb-2">
                  <Skeleton className="h-4 w-32" />
                </CardHeader>
                <CardContent>
                  <Skeleton className="mb-2 h-8 w-24" />
                  <Skeleton className="h-4 w-20" />
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Skeleton className="h-6 w-48" />
              <Skeleton className="h-10 w-64" />
            </div>

            <Card>
              <Table>
                <TableBody>
                  {[1, 2, 3, 4, 5].map((i) => (
                    <TableRow className="border-b-1 border-gray-200" key={i}>
                      {[1, 2, 3, 4, 5, 6].map((j) => (
                        <TableCell key={j}>
                          <Skeleton className="h-4 w-full" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full w-full flex-col bg-gray-50">
      <RebuildHeader />
      <div className="flex-1 space-y-6 overflow-y-auto p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <BarChart3 className="h-6 w-6 text-blue-500" />
            <h1 className="text-2xl font-bold">Value Add</h1>
          </div>

          {/* Organization Dropdown for Creator Users */}
          {isCreator && (
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600">Organization:</span>
              <Select value={selectedOrganization} onValueChange={setSelectedOrganization} disabled={isLoadingOrganizations}>
                <SelectTrigger className="w-64">
                  <SelectValue placeholder="Select organization" />
                </SelectTrigger>
                <SelectContent>
                  {isLoadingOrganizations ? (
                    <SelectItem value="loading" disabled>
                      Loading organizations...
                    </SelectItem>
                  ) : organizations.length > 0 ? (
                    organizations.map((org) => (
                      <SelectItem key={org.organizationId} value={org.organizationId}>
                        {org.organizationName}
                      </SelectItem>
                    ))
                  ) : (
                    <SelectItem value="no-orgs" disabled>
                      No organizations available
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {/* Recent Jobs Section */}
        <div className="space-y-4">
          <div className="mb-4 flex w-full items-center justify-between gap-4">
            <h2 className="text-xl font-semibold text-gray-900">
              {filteredEstimates.length > 0 && currentPage > 0
                ? `${filteredEstimates.length} Total Jobs`
                : `${Math.min(filteredEstimates.length, 10)} Most Recent Jobs`}
            </h2>
            <div className="w-96">
              <Input
                placeholder="Search by estimate name  ..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-96 border-gray-300 bg-white"
              />
            </div>
          </div>

          <div className="border border-gray-200 bg-white shadow-sm">
            <Table>
              <TableHeader>
                <TableRow className="border-b border-gray-300 bg-gray-50">
                  <TableHead className="w-8 py-3"></TableHead>
                  <TableHead className="py-3 text-xs font-semibold tracking-wider text-gray-600 uppercase">PROJECT NAME</TableHead>
                  <TableHead className="py-3 text-xs font-semibold tracking-wider text-gray-600 uppercase">STATUS</TableHead>
                  <TableHead className="py-3 text-xs font-semibold tracking-wider text-gray-600 uppercase">APPROVED VALUE</TableHead>
                  <TableHead className="py-3 text-xs font-semibold tracking-wider text-gray-600 uppercase">VALUE ADDED</TableHead>
                  <TableHead className="py-3 text-xs font-semibold tracking-wider text-gray-600 uppercase">% VALUE ADDED</TableHead>
                  <TableHead className="py-3 text-xs font-semibold tracking-wider text-gray-600 uppercase">ASSIGNEE</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedEstimates.map((estimate) => {
                  const isExpanded = expandedRows.has(estimate.id);
                  const hasLineItems = estimate.approvedRecommendations?.length > 0;

                  return (
                    <>
                      <TableRow key={estimate.id} className="border-b border-gray-200 hover:bg-gray-50">
                        <TableCell className="py-4">
                          {hasLineItems && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => toggleRowExpansion(estimate.id)}
                              className="h-6 w-6 p-1 hover:bg-gray-100"
                            >
                              {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                            </Button>
                          )}
                        </TableCell>
                        <TableCell className="py-4 font-medium">{estimate.rebuildEstimate?.projectName ?? "N/A"}</TableCell>
                        <TableCell className="py-4">
                          <div className="flex items-center">
                            <span className="mr-2 text-green-500">●</span>
                            <span className="text-sm text-gray-700">Completed</span>
                          </div>
                        </TableCell>
                        <TableCell className="py-4">${estimate.totalValue.toLocaleString()}</TableCell>
                        <TableCell className="py-4 font-semibold text-green-600">
                          +${estimate.rebuildRecommendationsValue.toLocaleString()}
                        </TableCell>
                        <TableCell className="py-4 font-semibold text-green-600">+{estimate.valueAddPercentage.toFixed(1)}%</TableCell>
                        <TableCell className="py-4">
                          {estimate.assigneeDetails ? `${estimate.assigneeDetails.firstName} ${estimate.assigneeDetails.lastName}` : "N/A"}
                        </TableCell>
                      </TableRow>

                      {isExpanded && hasLineItems && (
                        <TableRow className="border-b-0">
                          <TableCell colSpan={7} className="border-b-0 p-0">
                            <div className="bg-gray-50 py-4 pr-4 pl-12">
                              <h4 className="mb-4 text-sm font-semibold">Line Item Details</h4>
                              <div className="space-y-0">
                                {estimate.approvedRecommendations.map((item, index) => (
                                  <LineItemRow key={index} item={item} />
                                ))}
                              </div>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          {/* Pagination Controls */}
          {filteredEstimates.length > pageSize && (
            <div className="mt-6 flex items-center justify-between border border-gray-200 bg-white px-4 py-3">
              <div className="text-sm text-gray-700">
                {(currentPage - 1) * pageSize + 1} - {Math.min(currentPage * pageSize, filteredEstimates.length)} of{" "}
                {filteredEstimates.length} results
              </div>
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  onClick={() => currentPage > 1 && handlePageChange(currentPage - 1)}
                  disabled={currentPage <= 1}
                  className="text-sm"
                >
                  Back
                </Button>
                <Button
                  variant="outline"
                  onClick={() => currentPage < totalPages && handlePageChange(currentPage + 1)}
                  disabled={currentPage >= totalPages}
                  className="text-sm"
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
